import { useCallback, useMemo } from 'react';
import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import { Link } from 'react-router-dom';
import ExportMembersModal from '@/components/ExportMembersModal';
import NewStaffMemberFormModal from '@/components/NewStaffMemberFormModal';
import Pagination, { PaginationTypes } from '@/trio-ui/Pagination';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import { StaffMember } from '@/types/api/staffMember';
import { getMostRecentStaffMemberActiveJob } from '@/utils/staffMember';
import * as Styles from './Staff.styles';

type StaffProps = PaginationTypes & {
  members: StaffMember[];
  isLoading: boolean;
  isExportModalOpen?: boolean;
  onOpenExportModal?: () => void;
  onCloseExportModal?: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  isAddNewMemberModalOpen?: boolean;
  setIsAddNewMemberModalOpen?: (value: boolean) => void;
  onUpdateList?: () => void;
};

const Staff = function ({
  members,
  isLoading,
  onChangePageHandler,
  onChangeRecordsPerPage,
  count,
  itemsPerPage,
  lastPage,
  currentPage,
  isExportModalOpen = false,
  onOpenExportModal,
  onCloseExportModal,
  search,
  onSearchChange,
  isAddNewMemberModalOpen = false,
  setIsAddNewMemberModalOpen,
  onUpdateList,
}: StaffProps): JSX.Element {
  const renderHeader = useMemo(() => {
    return (
      <Tr>
        <Th>Profile</Th>
        <Th>Contact</Th>
        <Th>Area & Team</Th>
        <Th>Reporting</Th>
      </Tr>
    );
  }, []);

  const renderRow = useCallback((data: StaffMember) => {
    const monstRecentStaffMemberJob = getMostRecentStaffMemberActiveJob(data.jobInfo);

    return (
      <Tr key={data.id}>
        <Td className="tw-h-12">
          <div className="flex align-center">
            <Styles.StyledUserInitials
              size={48}
              pictureUrl={data.person.pictureUrl ?? ''}
              firstName={data.person.firstName}
              lastName={data.person.lastName ?? ''}
            />
            <Styles.UserNameAndRole>
              <Styles.UserNameContainer>
                <Link to={`/people/staff/${data.id}`}>
                  <Styles.UserName className="capitalize">
                    {data.person.firstName} {data.person.lastName}
                  </Styles.UserName>
                </Link>
                {data.person.employeeWorkStatus?.toLowerCase() === 'inactive' && (
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '1px 6px',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 500,
                      backgroundColor: '#FEE2E2',
                      color: '#DC2626',
                      border: '1px solid #FECACA',
                    }}
                  >
                    Inactive
                  </span>
                )}
                {data.person.country && <Styles.Tag>{data.person.country}</Styles.Tag>}
              </Styles.UserNameContainer>
              {monstRecentStaffMemberJob?.title && (
                <Styles.UserRole>{monstRecentStaffMemberJob?.title}</Styles.UserRole>
              )}
            </Styles.UserNameAndRole>
          </div>
        </Td>
        <Td>
          <Styles.TwoLineRow>
            {data.person.email && <Styles.TableSpan>{data.person.email}</Styles.TableSpan>}
            {data.person.mobilePhone && (
              <Styles.TableSpan>{data.person.mobilePhone}</Styles.TableSpan>
            )}
          </Styles.TwoLineRow>
        </Td>
        <Td>
          <Styles.TwoLineRow>
            {monstRecentStaffMemberJob?.area && (
              <Styles.SpanWithIcon>
                <MapsHomeWorkOutlinedIcon />
                <Styles.TableSpan>{monstRecentStaffMemberJob.area}</Styles.TableSpan>
              </Styles.SpanWithIcon>
            )}
            {monstRecentStaffMemberJob?.team?.name && (
              <Styles.SpanWithIcon>
                <WorkspacesOutlinedIcon />
                <Styles.TableSpan className="capitalize">
                  {monstRecentStaffMemberJob.team.name}
                </Styles.TableSpan>
              </Styles.SpanWithIcon>
            )}
          </Styles.TwoLineRow>
        </Td>
        <Td>
          <Styles.TwoLineRow>
            {data.manager && (
              <Styles.SpanWithIcon>
                <PersonPinOutlinedIcon />
                <Styles.TableSpan>{`Reports to ${data.manager.firstName}${
                  data.manager.lastName ? ` ${data.manager.lastName}` : ''
                }`}</Styles.TableSpan>
              </Styles.SpanWithIcon>
            )}
            {/* <Styles.SpanWithIcon>
              <SchemaOutlinedIcon />
              <Styles.TableSpan>phone number</Styles.TableSpan>
            </Styles.SpanWithIcon> */}
          </Styles.TwoLineRow>
        </Td>
      </Tr>
    );
  }, []);

  const renderEmptyStateRows = (i: number): JSX.Element => (
    <Tr key={i}>
      <Td className="tw-h-12">
        <div className="flex align-center">
          <SkeletonCircle width={12} height={12} className="tw-mr-3" />
          <Styles.UserNameAndRole>
            <div className="tw-flex tw-gap-2 tw-items-center">
              <Skeleton className="tw-h-4 tw-w-[80px] tw-my-0.5" />
              <Skeleton className="tw-h-4 tw-w-[40px] tw-my-0.5" />
            </div>
            <Skeleton className="tw-h-3.5 tw-w-[60px] tw-mt-1 tw-mb-[3px]" />
          </Styles.UserNameAndRole>
        </div>
      </Td>
      <Td>
        <Styles.TwoLineRow>
          <Skeleton className="tw-h-4 tw-w-[80px] tw-my-0.5" />
          <Skeleton className="tw-h-4 tw-w-[60px] tw-my-0.5" />
        </Styles.TwoLineRow>
      </Td>
      <Td>
        <Styles.SpanWithIcon>
          <Skeleton className="tw-h-[15px] tw-w-[15px]" />
          <Skeleton className="tw-h-3.5 tw-w-[80px]" />
        </Styles.SpanWithIcon>
      </Td>
      <Td>
        <Styles.SpanWithIcon>
          <Skeleton className="tw-h-[15px] tw-w-[15px]" />
          <Skeleton className="tw-h-3.5 tw-w-[100px]" />
        </Styles.SpanWithIcon>
      </Td>
    </Tr>
  );

  const handleFormSuccess = (): void => {
    onUpdateList?.();
    setIsAddNewMemberModalOpen?.(false);
  };

  return (
    <div data-testid="staff-page">
      <Styles.Header>
        <Styles.HeaderTitle>Staff</Styles.HeaderTitle>
        <Styles.Search value={search} onChangeHandler={(e) => onSearchChange(e.target.value)} />
        <Styles.Menu
          icon={KeyboardArrowDownOutlinedIcon}
          label="Export"
          menuItems={[
            {
              label: '.CSV file',
              callback: (e) => {
                e.stopPropagation();
                onOpenExportModal?.();
              },
            },
          ]}
        />
        <Styles.AddNew
          aria-label="New Team Member"
          onClick={() => setIsAddNewMemberModalOpen?.(true)}
        >
          <AddIcon />
        </Styles.AddNew>
      </Styles.Header>
      <Styles.TableContainer>
        {isLoading ? (
          <Table
            renderHeader={renderHeader}
            renderRow={renderEmptyStateRows}
            data={[...Array(itemsPerPage).keys()].map((key) => key + 1)}
            customBorderRadius={8}
          />
        ) : (
          <Table
            renderHeader={renderHeader}
            renderRow={renderRow}
            data={members}
            customBorderRadius={8}
          />
        )}
      </Styles.TableContainer>
      {!isLoading && (
        <Pagination
          onChangeRecordsPerPage={onChangeRecordsPerPage}
          lastPage={lastPage}
          itemsPerPage={itemsPerPage}
          count={count}
          currentPage={currentPage}
          onChangePageHandler={onChangePageHandler}
        />
      )}
      <ExportMembersModal
        isOpen={isExportModalOpen}
        onClose={() => {
          onCloseExportModal?.();
        }}
      />
      <NewStaffMemberFormModal
        isOpen={isAddNewMemberModalOpen}
        onClose={() => setIsAddNewMemberModalOpen?.(false)}
        onCreateStaffMember={handleFormSuccess}
      />
    </div>
  );
};

export default Staff;
